/**
 * CleaningStudio.js
 * Main data cleaning editor with Monaco-based Python editor,
 * version history, dry run preview, and execution controls.
 * Part of Phase 3: Data Cleaning Studio
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  Play,
  Save,
  History,
  Eye,
  EyeOff,
  RotateCcw,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Clock,
  Code,
  FileCode,
  Sparkles,
  Trash2,
  Download,
  RefreshCw,
  Loader,
  GitBranch,
  GitCommit,
  Zap,
  Database,
  ArrowRight,
  Info,
  Terminal,
  GripVertical,
  MessageSquare,
  Send,
  Shield,
  Edit3,
  MapPin,
  Copy,
  Layers,
  FileText
} from 'lucide-react';
import AILoadingOverlay from '../common/AILoadingOverlay';
import VerificationQueue from './VerificationQueue';
import './CleaningStudio.css';
import { transition } from 'd3';

// Monaco Editor - dynamically imported with error handling
const MonacoEditor = React.lazy(() => 
  import('@monaco-editor/react').then(module => ({ default: module.default })).catch(err => {
    console.warn('Monaco failed to load:', err);
    return { default: () => null };
  })
);

// Wrapper component for Monaco with error handling
class MonacoErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn('Monaco Error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError();
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

// Monaco Editor Wrapper with error handling
const MonacoEditorWrapper = ({ isDarkMode, scriptContent, setScriptContent, editorRef, onError }) => {
  const [loadError, setLoadError] = React.useState(false);
  
  React.useEffect(() => {
    // Set a timeout - if Monaco doesn't load in 5 seconds, fallback
    const timeout = setTimeout(() => {
      if (!editorRef.current) {
        console.warn('Monaco editor timeout - falling back to textarea');
        onError();
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [onError, editorRef]);
  
  if (loadError) {
    onError();
    return null;
  }
  
  return (
    <MonacoErrorBoundary onError={onError}>
      <MonacoEditor
        height="100%"
        language="python"
        theme={isDarkMode ? 'vs-dark' : 'light'}
        value={scriptContent}
        onChange={(value) => setScriptContent(value || '')}
        onMount={(editor) => { 
          editorRef.current = editor;
        }}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 4,
          wordWrap: 'on',
          padding: { top: 16 }
        }}
      />
    </MonacoErrorBoundary>
  );
};

const CleaningStudio = ({
  projectId,
  projectName,  // ADD: Project name for verification sessions
  qualityResults,
  checkResults,
  rawDataStats,
  columnTypes,  // var_name -> 'numeric' | 'date' | 'string' | etc.; powers Standardize ops
  xlsformData,  // Ship 3: { parseResult, matchingResult, fileName } — forwarded to VerificationQueue for choice/type validation
  data,         // Ship 3: raw row array — forwarded to VerificationQueue so issue rows can be displayed
  onNavigate,
  onCleaningComplete,
  // NEW: Simplified persistence - single saved state object and save callback
  savedCleaningStudioState = null,
  onSaveCleaningStudioState = null
}) => {
  const { isDarkMode } = useTheme();
  
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  
  // Script state - initialize from saved state
  const [scriptContent, setScriptContent] = useState(savedCleaningStudioState?.scriptContent || '');
  const [originalScript, setOriginalScript] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  
  // Key variables for cleaning operations - initialize from saved state
  const [keyVariables, setKeyVariables] = useState(savedCleaningStudioState?.keyVariables || {
    respondentId: '',
    collectorId: '',
    interviewDate: '',
    surveyUniqueKey: ''
  });
  const [showKeyVariables, setShowKeyVariables] = useState(true);
  
  // Version history - initialize from saved state
  const [versions, setVersions] = useState(savedCleaningStudioState?.versions || []);
  const [currentVersion, setCurrentVersion] = useState(savedCleaningStudioState?.currentVersion || null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Execution state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [executionResult, setExecutionResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Console/Output state - initialize from saved state
  const [consoleOutput, setConsoleOutput] = useState(savedCleaningStudioState?.consoleOutput || []);
  const [isExecuting, setIsExecuting] = useState(false);
  const consoleEndRef = useRef(null);
  
  // Resizable split pane
  const [editorWidth, setEditorWidth] = useState(60);
  const [isResizing, setIsResizing] = useState(false);
  const splitPaneRef = useRef(null);
  
  // Issues checklist - initialize from saved state
  const [selectedIssues, setSelectedIssues] = useState(savedCleaningStudioState?.selectedIssues || []);
  const [showIssues, setShowIssues] = useState(true);
  
  // Verified Exceptions - values confirmed as valid by field team
  const [verifiedExceptions, setVerifiedExceptions] = useState(savedCleaningStudioState?.verifiedExceptions || {});
  const [fieldCorrections, setFieldCorrections] = useState(savedCleaningStudioState?.fieldCorrections || {});
  const [showExceptions, setShowExceptions] = useState(false);
  
  // Field Verification Queue
  const [showVerificationQueue, setShowVerificationQueue] = useState(false);
  
  // Error state
  const [error, setError] = useState(null);
  
  // Monaco editor ref
  const editorRef = useRef(null);
  
  // Monaco loading state
  const [monacoError, setMonacoError] = useState(true);
  
  // Columns state
  const [dataColumns, setDataColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  
  // ============================================================================
  // PERSISTENCE: Helper to save current state to parent
  // Called at specific action points: script generation, save, dry run, run & clean
  // ============================================================================
  const saveCurrentState = useCallback(() => {
    if (onSaveCleaningStudioState) {
      const stateToSave = {
        keyVariables,
        selectedIssues,
        scriptContent,
        consoleOutput,
        versions,
        currentVersion,
        verifiedExceptions,
        fieldCorrections,
        savedAt: new Date().toISOString()
      };
      console.log('CleaningStudio: Saving state to parent', stateToSave);
      onSaveCleaningStudioState(stateToSave);
    }
  }, [keyVariables, selectedIssues, scriptContent, consoleOutput, versions, currentVersion, verifiedExceptions, fieldCorrections, onSaveCleaningStudioState]);
  
  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';
  
  // Helper to get auth headers
  const getAuthHeaders = useCallback(() => {
    let userEmail = '';
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userEmail = userInfo.email || '';
    } catch (e) {
      // Fallback to old key if parsing fails
      userEmail = localStorage.getItem('userEmail') || '';
    }
    return {
      'user_email': userEmail,
      'User-Email': userEmail,
      'Content-Type': 'application/json'
    };
  }, []);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  // Extract issues from quality results
  const availableIssues = React.useMemo(() => {
    const issues = [];
    const safeCheckResults = checkResults || {};

    // Helper to get data from either 'overall' subobject or root level
    const getCheckData = (check) => {
      if (!check) return null;
      // Handle nested structure: check.results.overall or check.overall or check
      if (check.results?.overall) return check.results.overall;
      if (check.overall) return check.overall;
      return check;
    };

    // ────────────────────────────────────────────────────────────────────────
    // STANDARDIZE — universal, always available (independent of detector results).
    // Bundles whitespace/Unicode/empty-row/empty-col/constant-col/numeric/date
    // ops into a single composite issue. Safe-by-default; the generated script
    // is fully editable if the user wants finer control.
    // ────────────────────────────────────────────────────────────────────────
    {
      const colCount = Object.keys(columnTypes || {}).length;
      const numericCount = Object.values(columnTypes || {}).filter(t =>
        typeof t === 'string' && ['numeric', 'integer', 'float', 'int', 'number', 'decimal'].includes(t.toLowerCase())
      ).length;
      const dateCount = Object.values(columnTypes || {}).filter(t =>
        typeof t === 'string' && ['date', 'datetime', 'timestamp', 'time'].includes(t.toLowerCase())
      ).length;
      const desc = colCount > 0
        ? `Whitespace, Unicode, drop empty/constant cols${numericCount ? ` • ${numericCount} numeric coerced` : ''}${dateCount ? ` • ${dateCount} dates → ISO 8601` : ''}`
        : 'Whitespace, Unicode normalize, drop empty rows·cols, drop constant cols';
      issues.push({
        id: 'standardize',
        label: 'Standardize Dataset',
        count: null,           // not a count-based issue
        severity: 'info',
        description: desc,
        correctable: true
      });
    }

    // DUPLICATES - uses total_duplicates
    if (safeCheckResults.duplicates) {
      const dupData = getCheckData(safeCheckResults.duplicates);
      const dupCount = dupData?.total_duplicates || 0;
      if (dupCount > 0) {
        issues.push({
          id: 'duplicates',
          label: 'Duplicates',
          count: dupCount,
          severity: dupCount > 100 ? 'critical' : 'warning',
          description: `Remove ${dupCount.toLocaleString()} duplicate values across variables`,
          correctable: true
        });
      }
    }
    
    // DURATION - uses outlier_count
    if (safeCheckResults.duration) {
      const durData = getCheckData(safeCheckResults.duration);
      const durationOutliers = durData?.outlier_count || 0;
      if (durationOutliers > 0) {
        issues.push({
          id: 'duration',
          label: 'Duration Outliers',
          count: durationOutliers,
          severity: 'warning',
          description: `Filter ${durationOutliers.toLocaleString()} interviews with unusual duration`,
          correctable: true
        });
      }
    }
    
    // OUTLIERS - structure is { overall: { variable_name: { outlier_count, ... } } }
    if (safeCheckResults.outliers) {
      const outlierData = getCheckData(safeCheckResults.outliers);
      let totalOutliers = 0;
      let varsWithOutliers = 0;
      
      if (outlierData) {
        Object.entries(outlierData).forEach(([key, value]) => {
          // Skip metadata keys: overall, by_collector, and keys starting with _
          if (key === 'overall' || key === 'by_collector' || key.startsWith('_')) return;
          if (value && typeof value === 'object' && 'outlier_count' in value) {
            totalOutliers += value.outlier_count || 0;
            if (value.outlier_count > 0) varsWithOutliers++;
          }
        });
      }
      
      if (totalOutliers > 0 || varsWithOutliers > 0) {
        issues.push({
          id: 'outliers',
          label: 'Statistical Outliers',
          count: totalOutliers || varsWithOutliers,
          severity: totalOutliers > 50 ? 'warning' : 'info',
          description: `Cap/handle outliers in ${varsWithOutliers} variable${varsWithOutliers !== 1 ? 's' : ''}`,
          correctable: true
        });
      }
    }
    
    // GEOLOCATION - check for suspicious clusters or locations
    if (safeCheckResults.geolocation) {
      const geoData = getCheckData(safeCheckResults.geolocation);
      const clusters = geoData?.cluster_count || 
                       geoData?.suspicious_clusters?.length || 
                       geoData?.suspicious_count || 0;
      const suspiciousLocations = geoData?.suspicious_locations?.length || 0;
      const totalGeoIssues = clusters + suspiciousLocations;
      
      if (totalGeoIssues > 0) {
        issues.push({
          id: 'geolocation',
          label: 'GPS Issues',
          count: totalGeoIssues,
          severity: totalGeoIssues > 20 ? 'warning' : 'info',
          description: `Flag ${totalGeoIssues} suspicious GPS location${totalGeoIssues !== 1 ? 's' : ''} for verification`,
          correctable: false,
          needsVerification: true
        });
      }
    }
    
    // MISSINGNESS/COMPLETENESS - look for variables with high missing rates
    if (safeCheckResults.missingness) {
      const missData = getCheckData(safeCheckResults.missingness);
      let criticalVars = 0;
      let highMissingVars = 0;
      
      // Handle both structures:
      // New: missData.per_variable or missData.by_variable
      // Old: variables directly at root level (filter out metadata keys)
      let perVar = missData?.per_variable || missData?.by_variable;
      
      // Fallback: extract variables directly from root (like CompletenessSection.js)
      if (!perVar && missData) {
        perVar = {};
        Object.entries(missData).forEach(([key, value]) => {
          // Skip metadata keys and non-variable objects
          if (key.startsWith('_') || key === 'overall' || key === 'by_collector' || 
              key === 'timeline_data' || key === 'has_timeline' || key === 'total_observations') {
            return;
          }
          // Check if it looks like a variable entry (has missing_percentage)
          if (value && typeof value === 'object' && 
              ('missing_percentage' in value || 'missing_percent' in value)) {
            perVar[key] = value;
          }
        });
      }
      
      if (perVar && Object.keys(perVar).length > 0) {
        Object.entries(perVar).forEach(([varName, data]) => {
          const missingPct = data?.missing_percent || data?.missing_percentage || 0;
          if (missingPct > 20) criticalVars++;
          else if (missingPct > 10) highMissingVars++;
        });
      }
      
      const totalIssueVars = criticalVars + highMissingVars;
      if (totalIssueVars > 0) {
        issues.push({
          id: 'missing',
          label: 'Missing Values',
          count: totalIssueVars,
          severity: criticalVars > 5 ? 'critical' : highMissingVars > 5 ? 'warning' : 'info',
          description: `Handle ${criticalVars} critical and ${highMissingVars} high-missing variables`,
          correctable: true
        });
      }
    }
    
    // SURVEY FLOW - check for violations
    if (safeCheckResults.survey_flow) {
      const flowData = getCheckData(safeCheckResults.survey_flow);
      const violations = flowData?.violation_count || 
                         flowData?.total_violations ||
                         flowData?.skip_logic_analysis?.violations?.length || 0;
      if (violations > 0) {
        issues.push({
          id: 'survey_flow',
          label: 'Skip Logic Violations',
          count: violations,
          severity: violations > 50 ? 'warning' : 'info',
          description: `Flag ${violations.toLocaleString()} skip logic violation${violations !== 1 ? 's' : ''}`,
          correctable: true
        });
      }
    }
    
    // TEXT CLUSTERING - similar responses and outliers
    if (safeCheckResults.text_clustering) {
      const textData = getCheckData(safeCheckResults.text_clustering);
      const similarPairs = textData?.total_similar_pairs || 
                           textData?.overall?.total_similar_pairs || 0;
      const textOutliers = textData?.total_outliers ||
                           textData?.overall?.total_outliers || 0;
      
      if (similarPairs > 0) {
        issues.push({
          id: 'similar_responses',
          label: 'Similar Responses',
          count: similarPairs,
          severity: similarPairs > 20 ? 'critical' : similarPairs > 5 ? 'warning' : 'info',
          description: `Review ${similarPairs} highly similar response pair${similarPairs !== 1 ? 's' : ''} for copying`,
          correctable: false,
          needsVerification: true
        });
      }
      
      if (textOutliers > 0) {
        issues.push({
          id: 'text_outliers',
          label: 'Atypical Responses',
          count: textOutliers,
          severity: 'info',
          description: `Review ${textOutliers} response${textOutliers !== 1 ? 's' : ''} that don't fit typical patterns`,
          correctable: false,
          needsVerification: true
        });
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // Coaching-loop detectors (added with the 11-detector rollout):
    // sentinel_phrases, gated_fields, probe_depth, straight_lining.
    // Sentinel + gated have auto-cleanable cases; probe_depth and straight-lining
    // are flag-only / verification-only.
    // ────────────────────────────────────────────────────────────────────────

    // SENTINEL PHRASES — short / placeholder / gibberish open-ended responses
    if (safeCheckResults.sentinel_phrases) {
      const sentData = getCheckData(safeCheckResults.sentinel_phrases);
      const flagged = sentData?.flagged_rows || [];
      // Only count auto-cleanable flag types (excludes dk_refused which is a real
      // answer, and encoding which is handled by the dedicated Encoding branch).
      const AUTO = new Set(['sentinel', 'short_response', 'gibberish']);
      const autoCount = flagged.filter(fr => AUTO.has(fr?.flag_type)).length;
      const totalCount = flagged.length;
      if (autoCount > 0) {
        issues.push({
          id: 'sentinel_phrases',
          label: 'Sentinel-Only Responses',
          count: autoCount,
          severity: autoCount > 50 ? 'warning' : 'info',
          description: `Blank ${autoCount.toLocaleString()} short / sentinel / gibberish response${autoCount !== 1 ? 's' : ''} so they show up as missing` +
            (totalCount > autoCount ? ` (${totalCount - autoCount} more flagged but not auto-fixable)` : ''),
          correctable: true
        });
      }
    }

    // GATED FIELDS — gate vs follow-up contradictions
    if (safeCheckResults.gated_fields) {
      const gateData = getCheckData(safeCheckResults.gated_fields);
      const flagged = gateData?.flagged_rows || [];
      const autoCount = flagged.filter(fr => fr?.flag_type === 'gate_no_unexpected_content').length;
      const flagOnlyCount = flagged.length - autoCount;
      if (flagged.length > 0) {
        const desc = autoCount > 0 && flagOnlyCount > 0
          ? `Auto-blank ${autoCount} follow-up cell${autoCount !== 1 ? 's' : ''} • flag ${flagOnlyCount} for review`
          : autoCount > 0
            ? `Auto-blank ${autoCount} follow-up cell${autoCount !== 1 ? 's' : ''} where gate said no but content was provided`
            : `Flag ${flagOnlyCount} contradiction${flagOnlyCount !== 1 ? 's' : ''} for supervisor review`;
        issues.push({
          id: 'gated_fields',
          label: 'Gated-Field Violations',
          count: flagged.length,
          severity: flagged.length > 50 ? 'warning' : 'info',
          description: desc,
          correctable: true
        });
      }
    }

    // PROBE DEPTH — low-specificity open-ended responses (verification-only)
    if (safeCheckResults.probe_depth) {
      const probeData = getCheckData(safeCheckResults.probe_depth);
      const flagged = probeData?.flagged_rows || [];
      if (flagged.length > 0) {
        issues.push({
          id: 'probe_depth',
          label: 'Low-Specificity Responses',
          count: flagged.length,
          severity: 'info',
          description: `Review ${flagged.length} response${flagged.length !== 1 ? 's' : ''} scoring low on specificity — cannot be auto-cleaned`,
          correctable: false,
          needsVerification: true
        });
      }
    }

    // STRAIGHT-LINING — flatlined Likert responses (whole respondent flag)
    if (safeCheckResults.straight_lining) {
      const slData = getCheckData(safeCheckResults.straight_lining);
      const flagged = slData?.flagged_rows || [];
      if (flagged.length > 0) {
        const sevCounts = flagged.reduce((acc, fr) => {
          const s = fr?.severity || 'low';
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});
        issues.push({
          id: 'straight_lining',
          label: 'Straight-Lining Patterns',
          count: flagged.length,
          severity: (sevCounts.high || 0) > 0 ? 'critical' : 'warning',
          description: `Flag ${flagged.length} respondent${flagged.length !== 1 ? 's' : ''} with flatlined responses ` +
            `(${sevCounts.high || 0} high, ${sevCounts.medium || 0} medium, ${sevCounts.low || 0} low). Adds _flag_straight_lining column.`,
          correctable: true
        });
      }
    }

    // BACKCHECK - discrepancies
    if (safeCheckResults.backcheck) {
      const backcheckData = getCheckData(safeCheckResults.backcheck);
      const discrepancies = backcheckData?.total_discrepancies ||
                            backcheckData?.discrepancy_count || 0;
      const highDiscrepancyVars = backcheckData?.high_discrepancy_variables?.length || 0;
      
      if (discrepancies > 0 || highDiscrepancyVars > 0) {
        issues.push({
          id: 'backcheck',
          label: 'Back-check Discrepancies',
          count: discrepancies || highDiscrepancyVars,
          severity: discrepancies > 50 ? 'critical' : 'warning',
          description: `Review ${discrepancies} discrepanc${discrepancies !== 1 ? 'ies' : 'y'} between original and back-check data`,
          correctable: true,
          needsVerification: true
        });
      }
    }
    
    // ENCODING ISSUES - from data summary or text analytics
    if (safeCheckResults.data_summary || rawDataStats) {
      let encodingIssues = 0;
      const source = safeCheckResults.data_summary || rawDataStats;
      
      if (source?.variables) {
        Object.values(source.variables).forEach(varData => {
          if (varData?.text_analytics?.cleaning_insights?.encoding_issues_count > 0) {
            encodingIssues += varData.text_analytics.cleaning_insights.encoding_issues_count;
          }
        });
      }
      
      if (encodingIssues > 0) {
        issues.push({
          id: 'encoding',
          label: 'Encoding Issues',
          count: encodingIssues,
          severity: encodingIssues > 20 ? 'warning' : 'info',
          description: `Fix ${encodingIssues} text encoding issue${encodingIssues !== 1 ? 's' : ''} (garbled characters)`,
          correctable: true
        });
      }
    }
    
    return issues;
  }, [checkResults, rawDataStats, columnTypes]);

  // ============================================================================
  // API CALLS
  // ============================================================================
  
  const fetchCurrentScript = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cleaning/scripts/${projectId}/current`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success && data.script) {
        setScriptContent(data.script.script_content);
        setOriginalScript(data.script.script_content);
        setCurrentVersion(data.script);
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Error fetching current script:', err);
    }
  }, [projectId, API_BASE, getAuthHeaders]);
  
  const fetchVersionHistory = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cleaning/scripts/${projectId}`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVersions(data.scripts || []);
      }
    } catch (err) {
      console.error('Error fetching version history:', err);
    }
  }, [projectId, API_BASE, getAuthHeaders]);
  
  // Fetch data columns from project
  const fetchDataColumns = useCallback(async () => {
    if (!projectId) return;
    
    // Skip API call if columns are already available from props
    if (rawDataStats?.columns?.length > 0) {
      setDataColumns(rawDataStats.columns);
      return;
    }
    
    setColumnsLoading(true);
    try {
      console.log('Fetching columns for project:', projectId);
      
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/state`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Columns are in data.data.columns or data.qualityChecksData.columns
        const columns = data?.data?.columns || data?.qualityChecksData?.columns || [];
        if (columns.length > 0) {
          setDataColumns(columns);
          console.log('Fetched columns from state:', columns.length, 'first 5:', columns.slice(0, 5));
        } else {
          console.warn('No columns found in state response');
        }
      } else {
        console.error('Failed to fetch state:', response.status);
      }
    } catch (err) {
      console.error('Error fetching data columns:', err);
    } finally {
      setColumnsLoading(false);
    }
  }, [projectId, API_BASE, getAuthHeaders]);
  
  const generateScript = async () => {
    // Validate key variables are set
    if (!keyVariables.respondentId) {
      setError('Please select a Respondent ID variable before generating the script.');
      setShowKeyVariables(true);
      return;
    }
    
    setIsGenerating(true);  // Show AILoadingOverlay
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/cleaning/generate-script`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          quality_results: checkResults || {},
          selected_issues: selectedIssues.length > 0 ? selectedIssues : null,
          key_variables: {
            respondent_id: keyVariables.respondentId,
            collector_id: keyVariables.collectorId || null,
            interview_date: keyVariables.interviewDate || null,
            survey_unique_key: keyVariables.surveyUniqueKey || null
          },
          // Pass verified exceptions and field corrections
          verified_exceptions: verifiedExceptions || {},
          field_corrections: fieldCorrections || {},
          // Pass column_types so the Standardize block can scope numeric coercion
          // and date parsing to the right columns instead of guessing at runtime.
          column_types: columnTypes || {}
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setScriptContent(data.script_content);
        setHasUnsavedChanges(true);
        
        // Auto-collapse cards to focus on script
        setShowKeyVariables(false);
        setShowIssues(false);
        
        // SAVE STATE: After generating script
        setTimeout(() => saveCurrentState(), 100);
      } else {
        setError(data.detail || 'Failed to generate script');
      }
    } catch (err) {
      setError(`Error generating script: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const saveScript = async () => {
    if (!scriptContent.trim()) {
      setError('Cannot save empty script');
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage('Saving script version...');
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/cleaning/save-script`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          script_content: scriptContent,
          commit_message: commitMessage || `Manual save at ${new Date().toLocaleTimeString()}`,
          is_auto_generated: false
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOriginalScript(scriptContent);
        setHasUnsavedChanges(false);
        setCommitMessage('');
        await fetchVersionHistory();
        await fetchCurrentScript();
        
        // SAVE STATE: After saving script version
        setTimeout(() => saveCurrentState(), 100);
      } else {
        setError(data.detail || 'Failed to save script');
      }
    } catch (err) {
      setError(`Error saving script: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const executeScript = async (dryRun = true) => {
    if (!scriptContent.trim()) {
      setError('No script to execute. Please generate or write a script first.');
      return;
    }
    
    // Clear console and start fresh
    setConsoleOutput([]);
    setIsExecuting(true);
    setError(null);
    setExecutionResult(null);
    
    // Auto-collapse cards to focus on execution output
    setShowKeyVariables(false);
    setShowIssues(false);
    
    // Helper to add console messages
    const addConsoleMessage = (type, message, lineNumber = null) => {
      const timestamp = new Date().toLocaleTimeString();
      setConsoleOutput(prev => [...prev, { type, message, lineNumber, timestamp }]);
    };
    
    addConsoleMessage('info', `Starting ${dryRun ? 'dry run' : 'cleaning execution'}...`);
    
    // Auto-save unsaved changes before executing
    if (hasUnsavedChanges) {
      addConsoleMessage('info', 'Auto-saving script before execution...');
      try {
        const saveResponse = await fetch(`${API_BASE}/api/cleaning/save-script`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            project_id: projectId,
            script_content: scriptContent,
            commit_message: 'Auto-save before execution'
          })
        });
        
        const saveData = await saveResponse.json();
        if (saveData.success) {
          setHasUnsavedChanges(false);
          setCurrentVersion(saveData.script);
          fetchVersionHistory();
          addConsoleMessage('success', `Script saved as v${saveData.script?.version || 'N/A'}`);
        }
      } catch (err) {
        console.error('Auto-save failed:', err);
        addConsoleMessage('warning', `Auto-save failed: ${err.message}`);
      }
    }
    
    addConsoleMessage('info', 'Executing Python script...');
    addConsoleMessage('code', 'import pandas as pd');
    addConsoleMessage('code', 'import numpy as np');
    addConsoleMessage('code', 'def clean_data(df): ...');
    
    try {
      const response = await fetch(`${API_BASE}/api/cleaning/execute`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          dry_run: dryRun
        })
      });
      
      const data = await response.json();
      setExecutionResult(data);
      setShowPreview(true);
      
      // Parse and display execution output in console
      if (data.success) {
        addConsoleMessage('success', '✓ Script executed successfully');
        
        // Show summary
        if (data.rows_before !== undefined) {
          addConsoleMessage('output', `Original rows: ${data.rows_before?.toLocaleString()}`);
        }
        if (data.rows_after !== undefined) {
          addConsoleMessage('output', `Final rows: ${data.rows_after?.toLocaleString()}`);
        }
        if (data.rows_removed !== undefined) {
          const pct = data.rows_before > 0 ? ((data.rows_removed / data.rows_before) * 100).toFixed(1) : 0;
          addConsoleMessage('output', `Rows removed: ${data.rows_removed?.toLocaleString()} (${pct}%)`);
        }
        
        // Show execution log if available
        if (data.execution_log) {
          const logLines = data.execution_log.split('\n').filter(line => line.trim());
          logLines.forEach(line => {
            if (line.includes('ERROR') || line.includes('Error')) {
              addConsoleMessage('error', line);
            } else if (line.includes('WARNING') || line.includes('Warning') || line.includes('⚠️')) {
              addConsoleMessage('warning', line);
            } else if (line.includes('Removed') || line.includes('Cleaned') || line.includes('✓')) {
              addConsoleMessage('success', line);
            } else {
              addConsoleMessage('output', line);
            }
          });
        }
        
        if (dryRun) {
          addConsoleMessage('info', '--- DRY RUN COMPLETE (no changes applied) ---');
          // SAVE STATE: After dry run
          setTimeout(() => saveCurrentState(), 100);
        } else {
          addConsoleMessage('success', '--- CLEANING COMPLETE ---');
          if (onCleaningComplete) {
            onCleaningComplete(data);
          }
          // SAVE STATE: After run & clean
          setTimeout(() => saveCurrentState(), 100);
        }
      } else {
        addConsoleMessage('error', `✗ Execution failed: ${data.detail || data.error || 'Unknown error'}`);
        
        // Show error details if available
        if (data.error_line) {
          addConsoleMessage('error', `Error at line ${data.error_line}`, data.error_line);
        }
        if (data.error_traceback) {
          data.error_traceback.split('\n').forEach(line => {
            if (line.trim()) addConsoleMessage('error', line);
          });
        }
      }
    } catch (err) {
      setError(`Error executing script: ${err.message}`);
      addConsoleMessage('error', `✗ Network error: ${err.message}`);
    } finally {
      setIsExecuting(false);
      addConsoleMessage('info', `Execution completed at ${new Date().toLocaleTimeString()}`);
    }
  };
  
  const loadVersion = async (version) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cleaning/scripts/${projectId}/version/${version}`,
        { headers: getAuthHeaders() }
      );
      
      const data = await response.json();
      
      if (data.success && data.script) {
        setScriptContent(data.script.script_content);
        setHasUnsavedChanges(data.script.version !== currentVersion?.version);
      }
    } catch (err) {
      setError(`Error loading version: ${err.message}`);
    }
  };
  
  const revertToVersion = async (version) => {
    try {
      await fetch(
        `${API_BASE}/api/cleaning/scripts/${projectId}/set-current/${version}`,
        { 
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      await fetchCurrentScript();
      await fetchVersionHistory();
      setShowVersionHistory(false);
    } catch (err) {
      setError(`Error reverting version: ${err.message}`);
    }
  };
  
  const resetVersionHistory = async () => {
    setIsLoading(true);
    setLoadingMessage('Resetting version history...');
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cleaning/scripts/${projectId}/reset`,
        { 
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        // Clear local state
        setVersions([]);
        setCurrentVersion(null);
        setScriptContent('');
        setOriginalScript('');
        setHasUnsavedChanges(false);
        setShowResetConfirm(false);
        setShowVersionHistory(false);
        
        // Refresh from server
        await fetchVersionHistory();
      } else {
        setError(data.detail || 'Failed to reset version history');
      }
    } catch (err) {
      setError(`Error resetting versions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Resizable pane handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !splitPaneRef.current) return;
    
    const container = splitPaneRef.current;
    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // Clamp between 30% and 80%
    const clampedWidth = Math.max(30, Math.min(80, newWidth));
    setEditorWidth(clampedWidth);
  }, [isResizing]);
  
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);
  
  // Add mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  useEffect(() => {
    fetchCurrentScript();
    fetchVersionHistory();
    fetchDataColumns();  // Fetch columns for dropdowns
  }, [fetchCurrentScript, fetchVersionHistory, fetchDataColumns]);
  
  useEffect(() => {
    if (scriptContent !== originalScript) {
      setHasUnsavedChanges(true);
    }
  }, [scriptContent, originalScript]);
  
  // Auto-detect key variables from available columns
  useEffect(() => {
    // Use dataColumns (fetched from API) or fallback to rawDataStats.columns
    const columns = dataColumns.length > 0 ? dataColumns : (rawDataStats?.columns || []);
    if (columns.length === 0) return;
    
    const columnLower = columns.map(c => c.toLowerCase());
    
    // Patterns for auto-detection
    const idPatterns = ['respondent_id', 'resp_id', 'hhid', 'hh_id', 'household_id', 'member_id', 'individual_id'];
    const collectorPatterns = ['collector', 'enumerator', 'interviewer', 'surveyor', 'enum_id', 'collector_id', 'data_collector', 'fieldworker'];
    const datePatterns = ['interview_date', 'date_interview', 'submission', 'start_time', 'starttime', 'date_collected', 'collection_date', 'survey_date', 'submissiondate'];
    const uniqueKeyPatterns = ['_uuid', 'uuid', '_key', 'key', '_submission_id', 'submission_id', 'instanceid', '_id', 'formhub/uuid', 'meta/instanceid'];
    
    // Find best match for respondent ID
    let foundRespondentId = '';
    for (const pattern of idPatterns) {
      const idx = columnLower.findIndex(c => c.includes(pattern));
      if (idx !== -1) {
        foundRespondentId = columns[idx];
        break;
      }
    }
    
    // Find best match for collector ID
    let foundCollectorId = '';
    for (const pattern of collectorPatterns) {
      const idx = columnLower.findIndex(c => c.includes(pattern));
      if (idx !== -1) {
        foundCollectorId = columns[idx];
        break;
      }
    }
    
    // Find best match for interview date
    let foundInterviewDate = '';
    for (const pattern of datePatterns) {
      const idx = columnLower.findIndex(c => c.includes(pattern));
      if (idx !== -1) {
        foundInterviewDate = columns[idx];
        break;
      }
    }
    
    // Find best match for survey unique key (auto-generated by KoBoToolbox, SurveyCTO, ODK)
    let foundSurveyUniqueKey = '';
    for (const pattern of uniqueKeyPatterns) {
      const idx = columnLower.findIndex(c => c === pattern || c.endsWith('/' + pattern) || c.includes(pattern));
      if (idx !== -1) {
        foundSurveyUniqueKey = columns[idx];
        break;
      }
    }
    
    // Update state if we found any
    setKeyVariables(prev => ({
      respondentId: prev.respondentId || foundRespondentId,
      collectorId: prev.collectorId || foundCollectorId,
      interviewDate: prev.interviewDate || foundInterviewDate,
      surveyUniqueKey: prev.surveyUniqueKey || foundSurveyUniqueKey
    }));
  }, [dataColumns, rawDataStats]);
  
  // Auto-scroll console to bottom when new messages are added

  {/*
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consoleOutput]);

  */}

  // Get available columns for dropdowns - use fetched columns or prop
  const availableColumns = React.useMemo(() => {
    if (dataColumns.length > 0) return dataColumns;
    return rawDataStats?.columns || [];
  }, [dataColumns, rawDataStats]);

  // ============================================================================
  // STYLES
  // ============================================================================
  
  const styles = {
    container: {
      minHeight: '100%',
      width: '100%',
      maxWidth: '100%',
      padding: isMobile ? '16px 12px' : '36px 40px 24px 40px',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    },

    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '16px',
    },

    titleSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flex: 1,
      minWidth: 0,
    },

    title: {
      fontSize: isMobile ? '20px' : '22px',
      fontWeight: '500',
      color: isDarkMode ? '#f0ede4' : '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      margin: 0,
      letterSpacing: '-0.02em',
      fontFamily: 'inherit',
    },

    subtitle: {
      fontSize: '12px',
      color: isDarkMode ? '#a8a49a' : '#555550',
      margin: 0,
      opacity: 0.75,
      fontWeight: 400,
    },

    headerActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      alignItems: 'center',
    },

    mainGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (showVersionHistory ? '1fr 280px' : '1fr'),
      gap: '12px',
      transition: 'all 0.3s ease',
    },

    editorSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },

    card: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
      overflow: 'hidden',
    },

    cardHeader: {
      padding: '10px 14px',
      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: 'transparent',
      minHeight: '40px',
    },

    cardTitle: {
      fontSize: '12px',
      fontWeight: '500',
      color: isDarkMode ? '#E5E7EB' : '#222222',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: 0,
      letterSpacing: '-0.005em',
    },

    editorContainer: {
      height: '500px',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '0 0 8px 8px',
    },

    // IDE Split Layout
    ideContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      height: isMobile ? 'auto' : '65vh',
      minHeight: isMobile ? '300px' : '500px',
      maxHeight: isMobile ? 'none' : '800px',
      borderRadius: '0',
      overflow: 'hidden',
      position: 'relative',
    },

    ideEditorPane: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      color: isDarkMode ? '#a8a49a' : '#555550',
    },

    ideResizeHandle: {
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s ease',
      flexShrink: 0,
    },

    ideConsolePane: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDarkMode ? '#0A0A0B' : '#FAFAFA',
      overflow: 'hidden',
      borderLeft: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
    },

    consoleHeader: {
      padding: '8px 12px',
      backgroundColor: 'transparent',
      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
      minHeight: '32px',
    },

    consoleTitle: {
      fontSize: '10px',
      fontWeight: '500',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      textTransform: 'lowercase',
      letterSpacing: '0.06em',
    },

    consoleBody: {
      flex: 1,
      overflow: 'auto',
      padding: '12px',
      fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
      fontSize: '12px',
      lineHeight: '1.6',
      backgroundColor: isDarkMode ? '#0A0A0B' : '#FFFFFF',
    },

    consoleLine: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginBottom: '4px',
      padding: '2px 0',
    },

    consoleTimestamp: {
      color: isDarkMode ? '#6b6862' : '#908c82',
      fontSize: '10px',
      minWidth: '65px',
      flexShrink: 0,
      fontFamily: 'inherit',
    },

    consoleMessage: {
      flex: 1,
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },

    unsavedBadge: {
      backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.15)' : 'rgba(217, 119, 6, 0.1)',
      color: '#D97706',
      padding: '3px 9px',
      borderRadius: '100px',
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '0.04em',
      textTransform: 'lowercase',
      border: `0.5px solid ${isDarkMode ? 'rgba(217, 119, 6, 0.3)' : 'rgba(217, 119, 6, 0.25)'}`,
    },

    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 14px',
      borderRadius: '100px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      letterSpacing: '-0.005em',
      fontFamily: 'inherit',
      whiteSpace: 'nowrap',
    },

    primaryButton: {
      backgroundColor: isDarkMode ? '#f0ede4' : '#1a1a1a',
      color: isDarkMode ? '#0D0E0E' : '#F8F5EE',
    },

    secondaryButton: {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      color: isDarkMode ? '#E5E7EB' : '#222222',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
    },

    dangerButton: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      border: `0.5px solid rgba(239, 68, 68, 0.25)`,
    },

    warningButton: {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      color: isDarkMode ? '#E5E7EB' : '#222222',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
    },

    iconButton: {
      padding: '6px',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
      color: isDarkMode ? '#a8a49a' : '#6B7280',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
    },

    issuesPanel: {
      padding: '12px 14px',
      width: '100%',
    },

    issueItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      borderRadius: '6px',
      marginBottom: '0',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
      flex: '1 1 0',
      minWidth: isMobile ? '0' : '180px',
    },

    issueCheckbox: {
      width: '16px',
      height: '16px',
      borderRadius: '3px',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'all 0.2s ease',
    },

    versionPanel: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
      overflow: 'hidden',
      height: 'fit-content',
      maxHeight: 'calc(100vh - 200px)',
      overflowY: 'auto',
    },

    versionItem: {
      padding: '12px 14px',
      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },

    previewPanel: {
      marginTop: '10px',
      padding: '16px',
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
    },

    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '10px',
      marginTop: '12px',
    },

    statCard: {
      padding: '12px 14px',
      borderRadius: '6px',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      textAlign: 'center',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
    },

    statValue: {
      fontSize: '18px',
      fontWeight: '500',
      marginBottom: '4px',
      letterSpacing: '-0.02em',
      fontVariantNumeric: 'tabular-nums',
    },

    statLabel: {
      fontSize: '10px',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
      textTransform: 'lowercase',
      letterSpacing: '0.06em',
      fontWeight: '500',
    },

    errorBanner: {
      backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.06)',
      color: isDarkMode ? '#F87171' : '#991b1b',
      padding: '10px 14px',
      borderRadius: '6px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      border: `0.5px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.2)'}`,
    },

    commitInput: {
      flex: 1,
      padding: '8px 12px',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
      color: isDarkMode ? '#E5E7EB' : '#222222',
      fontSize: '12px',
      outline: 'none',
      fontFamily: 'inherit',
    },
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#EF4444';
      case 'warning': return '#F59E0B';
      default: return '#2563EB';
    }
  };
  
  // Render Key Variables Selection Panel
  const renderKeyVariables = () => (
    <div style={styles.card}>
      <div 
        style={{...styles.cardHeader, cursor: 'pointer'}}
        onClick={() => setShowKeyVariables(!showKeyVariables)}
      >
        
        <h3 style={styles.cardTitle}>

          <Database size={14} style={{ color: isDarkMode ? '#a8a49a' : '#6B7280', opacity: 0.9 }} />
          Key Variables for Cleaning
          {keyVariables.respondentId && (
            <CheckCircle size={12} style={{ color: '#10B981', marginLeft: '6px' }} />
          )}
        </h3>
        {showKeyVariables ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        
      </div>
      
      
      {showKeyVariables && (
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <p style={{ 
            fontSize: '12px', 
            color: isDarkMode ? '#a8a49a' : '#6B7280',
            margin: '0 0 4px 0',
            lineHeight: 1.5,
          }}>
            Select the key variables used for identifying records. These are essential for accurate duplicate handling and targeted cleaning.
            {columnsLoading && (
              <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>Loading columns...</span>
            )}
            {!columnsLoading && availableColumns.length > 0 && (
              <span style={{ marginLeft: '8px', color: isDarkMode ? '#a8a49a' : '#555550', opacity: 0.85 }}>
                ({availableColumns.length} variables available)
              </span>
            )}
            {!columnsLoading && availableColumns.length === 0 && (
              <span style={{ marginLeft: '8px', color: '#D97706' }}>
                (No columns found - please ensure data is loaded)
              </span>
            )}
          </p>
          
          <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row'}}>
          {/* Respondent ID */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ 
              fontSize: '13px', 
              fontWeight: '500',
              color: isDarkMode ? '#E5E7EB' : '#222222',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Respondent Unique ID
              <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <select
              value={keyVariables.respondentId}
              onChange={(e) => setKeyVariables(prev => ({ ...prev, respondentId: e.target.value }))}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                color: isDarkMode ? '#E5E7EB' : '#222222',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">-- Select Respondent ID --</option>
              {availableColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            <span style={{ fontSize: '11px', color: isDarkMode ? '#6b6862' : '#908c82' }}>
              The unique identifier for each respondent/household (e.g., household_id, resp_id)
            </span>
          </div>
          
          {/* Data Collector ID */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ 
              fontSize: '13px', 
              fontWeight: '500',
              color: isDarkMode ? '#E5E7EB' : '#222222'
            }}>
              Data Collector ID
            </label>
            <select
              value={keyVariables.collectorId}
              onChange={(e) => setKeyVariables(prev => ({ ...prev, collectorId: e.target.value }))}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                color: isDarkMode ? '#E5E7EB' : '#222222',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">-- Select Collector ID (optional) --</option>
              {availableColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            <span style={{ fontSize: '11px', color: isDarkMode ? '#6b6862' : '#908c82' }}>
              Helps identify which enumerator collected each record
            </span>
          </div>
          
          {/* Interview Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ 
              fontSize: '13px', 
              fontWeight: '500',
              color: isDarkMode ? '#E5E7EB' : '#222222'
            }}>
              Interview Date
            </label>
            <select
              value={keyVariables.interviewDate}
              onChange={(e) => setKeyVariables(prev => ({ ...prev, interviewDate: e.target.value }))}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                color: isDarkMode ? '#E5E7EB' : '#222222',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">-- Select Interview Date (optional) --</option>
              {availableColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            <span style={{ fontSize: '11px', color: isDarkMode ? '#6b6862' : '#908c82' }}>
              Used to determine which record to keep when handling duplicates (earliest/latest)
            </span>
          </div>
          
          {/* Survey Unique Key */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ 
              fontSize: '13px', 
              fontWeight: '500',
              color: isDarkMode ? '#E5E7EB' : '#222222'
            }}>
              Survey Unique Key
            </label>
            <select
              value={keyVariables.surveyUniqueKey}
              onChange={(e) => setKeyVariables(prev => ({ ...prev, surveyUniqueKey: e.target.value }))}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                color: isDarkMode ? '#E5E7EB' : '#222222',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">-- Select Survey Unique Key (optional) --</option>
              {availableColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            <span style={{ fontSize: '11px', color: isDarkMode ? '#6b6862' : '#908c82' }}>
              Auto-generated by KoBoToolbox, SurveyCTO, ODK (e.g., _uuid, KEY, _submission_id). Uniquely identifies each submission.
            </span>
          </div>

          </div>
          
          {/* Status indicator */}
          {!keyVariables.respondentId && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.3)' : '#FECACA'}`
            }}>
              <AlertCircle size={16} style={{ color: '#EF4444' }} />
              <span style={{ fontSize: '13px', color: '#EF4444' }}>
                Respondent ID is required to generate cleaning script
              </span>
            </div>
          )}
        </div>
      )}
      
    </div>
  );
  
  const renderIssuesList = () => (
    
    <div style={styles.card}>
      
      <div 
        style={styles.cardHeader}
        onClick={() => setShowIssues(!showIssues)}
      >
        <h3 style={styles.cardTitle}>
          <AlertTriangle size={14} style={{ color: '#F59E0B' }} />
          Issues to Address ({availableIssues.length})
        </h3>
        {showIssues ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      
      {showIssues && (
        <div style={styles.issuesPanel}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', width: '100%' }}>
          
          {availableIssues.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              color: isDarkMode ? '#a8a49a' : '#6B7280'
            }}>
              <CheckCircle size={32} style={{ marginBottom: '8px', color: '#10B981' }} />
              <p>No significant issues detected!</p>
            </div>
            
          ) : (
            availableIssues.map((issue) => {
              const isSelected = selectedIssues.includes(issue.id);
              const isVerificationOnly = issue.correctable === false && issue.needsVerification;
              
              return (
                <div
                  key={issue.id}
                  style={{
                    ...styles.issueItem,
                    borderColor: isSelected
                      ? (isDarkMode ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.18)')
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'),
                    backgroundColor: isSelected
                      ? (isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
                      : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
                    opacity: issue.correctable === false && !issue.needsVerification ? 0.65 : 1,
                    cursor: isVerificationOnly ? 'default' : 'pointer'
                  }}
                  onClick={() => {
                    // Only allow selection for correctable issues
                    if (issue.correctable !== false) {
                      if (isSelected) {
                        setSelectedIssues(selectedIssues.filter(id => id !== issue.id));
                      } else {
                        setSelectedIssues([...selectedIssues, issue.id]);
                      }
                    }
                  }}
                >
                  {/* Only show checkbox for correctable issues */}
                  {!isVerificationOnly && (
                    <div style={{
                      ...styles.issueCheckbox,
                      backgroundColor: isSelected ? (isDarkMode ? '#f0ede4' : '#1a1a1a') : 'transparent',
                      borderColor: isSelected ? (isDarkMode ? '#f0ede4' : '#1a1a1a') : (isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
                      cursor: 'pointer'
                    }}>
                      {isSelected && <Check size={12} color={isDarkMode ? '#0D0E0E' : '#F8F5EE'} strokeWidth={2.5} />}
                    </div>
                  )}
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '4px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontWeight: '500',
                        color: isDarkMode ? '#E5E7EB' : '#222222',
                        fontSize: '13px',
                        letterSpacing: '-0.005em'
                      }}>
                        {issue.label}
                      </span>
                      <span style={{
                        backgroundColor: 'transparent',
                        color: getSeverityColor(issue.severity),
                        padding: '1px 8px',
                        borderRadius: '100px',
                        fontSize: '10px',
                        fontWeight: '500',
                        letterSpacing: '0.02em',
                        fontVariantNumeric: 'tabular-nums',
                        border: `0.5px solid ${getSeverityColor(issue.severity)}40`,
                      }}>
                        {issue.count}
                      </span>
                      {issue.needsVerification && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            setShowVerificationQueue(true);
                          }}
                          style={{
                            backgroundColor: isDarkMode ? '#2563EB20' : '#DBEAFE',
                            color: '#2563EB',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = isDarkMode ? '#2563EB40' : '#BFDBFE';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isDarkMode ? '#2563EB20' : '#DBEAFE';
                          }}
                        >
                          <MessageSquare size={10} />
                          Verify
                        </button>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '13px',
                      color: isDarkMode ? '#a8a49a' : '#6B7280'
                    }}>
                      {issue.description}
                    </div>
                  </div>
                </div>
              
            );
            
            })
            
          )}
          </div>
          
          {availableIssues.length > 0 && (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
            <button
              style={{
                ...styles.button,
                ...styles.primaryButton,
                minWidth: '220px',
                justifyContent: 'center',
                marginTop: '12px',
                opacity: isGenerating ? 0.5 : 1,
              }}
              onClick={generateScript}
              disabled={isGenerating}
            >
              <Sparkles size={14} />
              Generate Cleaning Script
            </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Render Verified Exceptions section
  const renderVerifiedExceptions = () => {
    const exceptionCount = Object.keys(verifiedExceptions).length;
    const correctionCount = Object.keys(fieldCorrections).length;
    const totalCount = exceptionCount + correctionCount;
    
    // Always show this panel so the Verification Queue button is accessible
    return (
      <div style={styles.card}>
        <div 
          style={styles.cardHeader}
          onClick={() => setShowExceptions(!showExceptions)}
        >
          <h3 style={styles.cardTitle}>
            <Shield size={14} style={{ color: '#10B981' }} />
            Field Verification {totalCount > 0 && `(${totalCount})`}
          </h3>
          {showExceptions ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
        
        {showExceptions && (
          <div style={{ padding: '16px' }}>
            <p style={{ 
              fontSize: '13px', 
              color: isDarkMode ? '#a8a49a' : '#6B7280',
              marginBottom: '12px'
            }}>
              Values verified or corrected by field team. These will be incorporated into cleaning.
            </p>
            
            {totalCount === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px',
                color: isDarkMode ? '#a8a49a' : '#6B7280',
                backgroundColor: isDarkMode ? '#242424' : '#f9fafb',
                borderRadius: '8px'
              }}>
                <CheckCircle size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '13px' }}>No verifications yet</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>
                  Use the Verification Queue to confirm outliers or provide corrections
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Verified Valid Exceptions */}
                {Object.entries(verifiedExceptions).map(([id, exception]) => (
                  <div 
                    key={id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: isDarkMode ? '#10B98115' : '#ECFDF5',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#10B98130' : '#A7F3D0'}`
                    }}
                  >
                    <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '500',
                        color: isDarkMode ? '#E5E7EB' : '#1F2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        Row {exception.rowIndex} • {exception.variable}
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          backgroundColor: '#10B98120',
                          color: '#10B981'
                        }}>Valid</span>
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: isDarkMode ? '#a8a49a' : '#6B7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        Value: {String(exception.value).substring(0, 50)}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const newExceptions = { ...verifiedExceptions };
                        delete newExceptions[id];
                        setVerifiedExceptions(newExceptions);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: isDarkMode ? '#a8a49a' : '#6B7280'
                      }}
                      title="Remove exception"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                
                {/* Field Corrections */}
                {Object.entries(fieldCorrections).map(([id, correction]) => (
                  <div 
                    key={id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: isDarkMode ? '#8B5CF615' : '#EDE9FE',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#8B5CF630' : '#C4B5FD'}`
                    }}
                  >
                    <Edit3 size={16} style={{ color: '#8B5CF6', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '500',
                        color: isDarkMode ? '#E5E7EB' : '#1F2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        Row {correction.rowIndex} • {correction.variable}
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          backgroundColor: '#8B5CF620',
                          color: '#8B5CF6'
                        }}>Corrected</span>
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: isDarkMode ? '#a8a49a' : '#6B7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        New value: {String(correction.correction).substring(0, 50)}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const newCorrections = { ...fieldCorrections };
                        delete newCorrections[id];
                        setFieldCorrections(newCorrections);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: isDarkMode ? '#a8a49a' : '#6B7280'
                      }}
                      title="Remove correction"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Button to open Verification Queue */}
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
            <button
              onClick={() => setShowVerificationQueue(true)}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                minWidth: '220px',
                justifyContent: 'center',
                marginTop: '12px',
              }}

            >
              <MessageSquare size={14} />
              Open Verification Queue
            </button>
            </div>
          </div>
        )}
        
        {/* Show button even when collapsed */}
        {!showExceptions && (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
          <div style={{ padding: '10px 14px', borderTop: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => setShowVerificationQueue(true)}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                minWidth: '220px',
                justifyContent: 'center',
              }}
            >
              <MessageSquare size={14} />
              Open Verification Queue
            </button>
          </div>
          </div>
        )}
      </div>
      
    );
  };
  
  const renderVersionHistory = () => (
    <div style={styles.versionPanel}>
      <div style={styles.cardHeader}>
        <h3 style={styles.cardTitle}>
          <History size={14} style={{ color: isDarkMode ? '#a8a49a' : '#6B7280', opacity: 0.9 }} />
          Version History
        </h3>
        <button
          style={styles.iconButton}
          onClick={() => setShowVersionHistory(false)}
        >
          <X size={16} />
        </button>
      </div>
      
      {versions.length === 0 ? (
        <div style={{ 
          padding: '18px', 
          textAlign: 'center',
          color: isDarkMode ? '#a8a49a' : '#6B7280'
        }}>
          <GitBranch size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <p>No versions saved yet</p>
        </div>
      ) : (
        versions.map((version) => (
          <div
            key={version.id}
            style={{
              ...styles.versionItem,
              backgroundColor: version.is_current 
                ? (isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)')
                : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!version.is_current) {
                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
              }
            }}
            onMouseLeave={(e) => {
              if (!version.is_current) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '6px'
            }}>
              <span style={{ 
                fontWeight: '600',
                color: isDarkMode ? '#E5E7EB' : '#222222',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <GitCommit size={14} />
                v{version.version}
                {version.is_current && (
                  <span style={{
                    backgroundColor: '#10B981',
                    color: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    CURRENT
                  </span>
                )}
              </span>
            </div>
            
            <div style={{ 
              fontSize: '12px',
              color: isDarkMode ? '#a8a49a' : '#6B7280',
              marginBottom: '8px'
            }}>
              {version.commit_message || 'No message'}
            </div>
            
            <div style={{ 
              fontSize: '11px',
              color: isDarkMode ? '#6b6862' : '#908c82',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <Clock size={12} />
              {version.created_at 
                ? new Date(version.created_at).toLocaleString()
                : 'Unknown date'}
              {version.is_auto_generated && (
                <span style={{
                  backgroundColor: '#8B5CF620',
                  color: '#8B5CF6',
                  padding: '1px 6px',
                  borderRadius: '8px',
                  fontSize: '10px'
                }}>
                  Auto
                </span>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  padding: '6px 10px',
                  fontSize: '12px'
                }}
                onClick={() => loadVersion(version.version)}
              >
                <Eye size={12} />
                View
              </button>
              
              {!version.is_current && (
                <button
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    padding: '6px 10px',
                    fontSize: '12px'
                  }}
                  onClick={() => revertToVersion(version.version)}
                >
                  <RotateCcw size={12} />
                  Revert
                </button>
              )}
            </div>
          </div>
        ))
      )}
      
      {/* Reset All Versions Button */}
      {versions.length > 0 && (
        <div style={{ 
          padding: '16px', 
          borderTop: `1px solid ${isDarkMode ? '#333' : '#e5e7eb'}` 
        }}>
          {!showResetConfirm ? (
            <button
              style={{
                ...styles.button,
                width: '100%',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: `1px solid ${isDarkMode ? '#EF444450' : '#EF444450'}`,
                color: '#EF4444',
                fontSize: '12px',
                padding: '8px 12px'
              }}
              onClick={() => setShowResetConfirm(true)}
            >
              <Trash2 size={14} />
              Reset All Versions
            </button>
          ) : (
            <div style={{
              backgroundColor: isDarkMode ? '#EF444415' : '#FEF2F2',
              border: `1px solid ${isDarkMode ? '#EF444450' : '#FECACA'}`,
              borderRadius: '8px',
              padding: '12px'
            }}>
              <p style={{ 
                fontSize: '12px', 
                color: isDarkMode ? '#FCA5A5' : '#DC2626',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                <AlertTriangle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                This will permanently delete all {versions.length} version(s). This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    flex: 1,
                    justifyContent: 'center',
                    fontSize: '12px',
                    padding: '8px'
                  }}
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.button,
                    flex: 1,
                    justifyContent: 'center',
                    backgroundColor: '#EF4444',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '12px',
                    padding: '8px'
                  }}
                  onClick={resetVersionHistory}
                  disabled={isLoading}
                >
                  <Trash2 size={12} />
                  {isLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  const renderExecutionPreview = () => {
    if (!executionResult) return null;
    
    return (
      <div style={styles.previewPanel}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h3 style={styles.cardTitle}>
            {executionResult.success ? (
              <>
                <CheckCircle size={20} style={{ color: '#10B981' }} />
                Execution Preview
              </>
            ) : (
              <>
                <AlertCircle size={20} style={{ color: '#EF4444' }} />
                Execution Failed
              </>
            )}
          </h3>
          <button
            style={styles.iconButton}
            onClick={() => setShowPreview(false)}
          >
            <X size={16} />
          </button>
        </div>
        
        {executionResult.error ? (
          <div style={styles.errorBanner}>
            <AlertCircle size={18} />
            {executionResult.error}
          </div>
        ) : (
          <>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: isDarkMode ? '#ffffff' : '#1a1a1aff' }}>
                  {executionResult.rows_before?.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Rows Before</div>
              </div>
              
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#10B981' }}>
                  {executionResult.rows_after?.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Rows After</div>
              </div>
              
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#EF4444' }}>
                  -{executionResult.rows_removed?.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Rows Removed</div>
              </div>
              
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#F59E0B' }}>
                  {executionResult.values_modified?.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Values Modified</div>
              </div>
              
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#8B5CF6' }}>
                  {executionResult.execution_time_ms}ms
                </div>
                <div style={styles.statLabel}>Execution Time</div>
              </div>
            </div>
            
            {/* Execution Log Output (if available) */}
            {executionResult.execution_log && executionResult.execution_log.trim() && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#f3f4f6',
                borderRadius: '8px',
                fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace",
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflowY: 'auto',
                color: isDarkMode ? '#d4d4d4' : '#222222ff'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: isDarkMode ? '#a8a49a' : '#6B7280'
                }}>
                  Execution Log:
                </div>
                {executionResult.execution_log}
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '20px',
              justifyContent: 'flex-end'
            }}>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton
                }}
                onClick={() => executeScript(true)}
                disabled={isExecuting || isGenerating}
              >
                <RefreshCw size={16} />
                Refresh Preview
              </button>
              
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton
                }}
                onClick={() => executeScript(false)}
                disabled={isExecuting || isGenerating}
              >
                <Zap size={16} />
                Apply Cleaning
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <>
      {/* AILoadingOverlay - only for script generation */}
      <AILoadingOverlay 
        isVisible={isGenerating}
        context="cleaning"
        title="Generating Cleaning Script"
        subtitle="Building a tailored cleaning script based on your quality findings"
        realTimeStats={{
          totalRows: rawDataStats?.total_rows || rawDataStats?.row_count || 0,
          columnsAnalyzed: rawDataStats?.columns?.length || 0,
        }}
        showDetailedLogs={true}
      />
      
      <div className='cleaningcont' style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>
              <Code size={18} style={{ color: isDarkMode ? '#f0ede4' : '#1a1a1a', opacity: 0.85 }} />
              Data Cleaning Studio
            </h1>
            <p style={styles.subtitle}>
              Create and manage Python cleaning scripts with version control
            </p>
          </div>
          
          <div style={styles.headerActions}>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton
              }}
              onClick={() => setShowVersionHistory(!showVersionHistory)}
            >
              <History size={16} />
              {showVersionHistory ? 'Hide History' : 'Show History'}
            </button>
            
            <button
              style={{
                ...styles.button,
                ...styles.warningButton
              }}
              onClick={() => executeScript(true)}
              disabled={isExecuting || isGenerating || !scriptContent.trim()}
            >
              <Eye size={16} />
              Dry Run
            </button>
            
            <button
              style={{
                ...styles.button,
                ...styles.primaryButton
              }}
              onClick={() => executeScript(false)}
              disabled={isExecuting || isGenerating || !scriptContent.trim()}
            >
              <Play size={16} />
              Run & Clean
            </button>
          </div>
        </div>
        
        {/* Error Banner */}
        {error && (
          <div style={styles.errorBanner}>
            <AlertCircle size={18} />
            {error}
            <button
              style={{ 
                marginLeft: 'auto', 
                background: 'none', 
                border: 'none',
                cursor: 'pointer',
                color: 'inherit'
              }}
              onClick={() => setError(null)}
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {/* Main Grid */}
        <div style={styles.mainGrid}>
          <div style={styles.editorSection}>
            {/* Key Variables Panel */}
            {renderKeyVariables()}
            
            {/* Field Verification Panel - verify first! */}
            {renderVerifiedExceptions()}
            
            {/* Issues Panel - then generate cleaning script */}
            {renderIssuesList()}
            
            {/* Editor Card with IDE Split Layout */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <FileCode size={14} style={{ color: isDarkMode ? '#a8a49a' : '#6B7280', opacity: 0.9 }} />
                  Cleaning Script
                  {currentVersion && (
                    <span style={{ 
                      fontWeight: '400',
                      color: isDarkMode ? '#a8a49a' : '#6B7280',
                      fontSize: '13px'
                    }}>
                      (v{currentVersion.version})
                    </span>
                  )}
                </h3>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {hasUnsavedChanges && (
                    <span style={styles.unsavedBadge}>Unsaved Changes</span>
                  )}
                </div>
              </div>
              
              {/* IDE Split Pane Layout */}
              <div 
                ref={splitPaneRef}
                style={styles.ideContainer}
              >
                {/* Left: Script Editor */}
                <div style={{ ...styles.ideEditorPane, width: isMobile ? '100%' : `${editorWidth}%` }}>
                  {!monacoError ? (
                    <React.Suspense fallback={
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        color: isDarkMode ? '#a8a49a' : '#6B7280'
                      }}>
                        <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                        Loading editor...
                      </div>
                    }>
                      <MonacoEditorWrapper
                        isDarkMode={isDarkMode}
                        scriptContent={scriptContent}
                        setScriptContent={setScriptContent}
                        editorRef={editorRef}
                        onError={() => setMonacoError(true)}
                      />
                    </React.Suspense>
                  ) : (
                    /* Fallback textarea when Monaco fails to load */
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/*
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: isDarkMode ? '#332200' : '#FEF3C7',
                        color: isDarkMode ? '#FCD34D' : '#92400E',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: 0
                      }}>
                        
                        <AlertTriangle size={14} />
                        Advanced editor unavailable. Using simple editor mode.
                        
                      </div>
                      */}
                      <textarea
                        value={scriptContent}
                        onChange={(e) => setScriptContent(e.target.value)}
                        placeholder={`# Enter your Python cleaning script here...\n\nimport pandas as pd\n\ndef clean_data(df):\n    # Your cleaning logic here\n    return df`}
                        spellCheck={false}
                        style={{
                          flex: 1,
                          width: '100%',
                          padding: '16px',
                          fontFamily: "'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace",
                          fontSize: '13px',
                          lineHeight: '1.6',
                          border: 'none',
                          outline: 'none',
                          resize: 'none',
                          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                          color: isDarkMode ? '#E5E7EB' : '#222222',
                          tabSize: 4,
                          whiteSpace: 'pre',
                          overflowX: 'auto',
                          overflowY: 'auto',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                </div>
                
                {/* Resize Handle */}
                {!isMobile && (
                <div
                  onMouseDown={handleMouseDown}
                  style={{
                    width: '8px',
                    cursor: 'col-resize',
                    backgroundColor: isResizing 
                      ? (isDarkMode ? '#f0ede4' : '#1a1a1a')
                      : (isDarkMode ? '#333' : '#e5e7eb'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: isResizing ? 'none' : 'background-color 0.2s ease',
                    flexShrink: 0,
                    userSelect: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isResizing) e.currentTarget.style.backgroundColor = isDarkMode ? '#444' : '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    if (!isResizing) e.currentTarget.style.backgroundColor = isDarkMode ? '#333' : '#e5e7eb';
                  }}
                >
                  <GripVertical size={14} style={{ color: isDarkMode ? '#666' : '#9ca3af' }} />
                </div>
                )}
                
                {/* Right: Console/Output */}
                <div style={{ ...styles.ideConsolePane, width: isMobile ? '100%' : `${100 - editorWidth}%` }}>
                  <div style={styles.consoleHeader}>
                    <span style={styles.consoleTitle}>
                      <Terminal size={14} />
                      Output Console
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {isExecuting && (
                        <span style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          color: '#10B981',
                          fontSize: '11px'
                        }}>
                          <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          Running...
                        </span>
                      )}
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isDarkMode ? '#6B7280' : '#94a3b8',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => setConsoleOutput([])}
                        title="Clear console"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.consoleBody}>
                    {consoleOutput.length === 0 ? (
                      <div style={{ 
                        color: isDarkMode ? '#6B7280' : '#94a3b8', 
                        textAlign: 'center', 
                        paddingTop: '40px',
                        fontSize: '12px'
                      }}>
                        <Terminal size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                        <p style={{ margin: 0 }}>Output will appear here</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '11px', opacity: 0.7 }}>
                          Click "Dry Run" or "Run & Clean" to execute
                        </p>
                      </div>
                    ) : (
                      consoleOutput.map((line, index) => {
                        // Determine color based on message type and theme
                        let color = isDarkMode ? '#d4d4d4' : '#222222ff';  // default output
                        let prefix = '';
                        let bgColor = 'transparent';
                        
                        switch (line.type) {
                          case 'error':
                            color = isDarkMode ? '#F87171' : '#DC2626';
                            prefix = '✗ ';
                            bgColor = isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)';
                            break;
                          case 'warning':
                            color = isDarkMode ? '#FBBF24' : '#D97706';
                            prefix = '⚠ ';
                            bgColor = isDarkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(251, 191, 36, 0.08)';
                            break;
                          case 'success':
                            color = isDarkMode ? '#34D399' : '#059669';
                            prefix = '✓ ';
                            break;
                          case 'info':
                            color = isDarkMode ? '#60A5FA' : '#1A6FE0';
                            prefix = '→ ';
                            break;
                          case 'code':
                            color = isDarkMode ? '#A78BFA' : '#7C3AED';
                            prefix = '  ';
                            break;
                          default:
                            color = isDarkMode ? '#a8a49a' : '#6B7280';
                            prefix = '  ';
                        }
                        
                        return (
                          <div 
                            key={index} 
                            style={{
                              ...styles.consoleLine,
                              backgroundColor: bgColor,
                              borderRadius: '8px',
                              padding: bgColor !== 'transparent' ? '4px 8px' : '2px 0'
                            }}
                          >
                            <span style={styles.consoleTimestamp}>
                              {line.timestamp}
                            </span>
                            <span style={{ 
                              ...styles.consoleMessage,
                              color 
                            }}>
                              {prefix}{line.message}
                              {line.lineNumber && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  color: isDarkMode ? '#6b6862' : '#908c82',
                                  fontSize: '10px'
                                }}>
                                  (line {line.lineNumber})
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })
                    )}
                    <div ref={consoleEndRef} />
                  </div>
                </div>
              </div>
              
              {/* Save Bar */}
              <div style={{
                padding: '10px 14px',
                borderTop: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                backgroundColor: 'transparent'
              }}>
                <input
                  type="text"
                  placeholder="Commit message (optional)"
                  value={commitMessage}
                  onChange={(e) => setCommitMessage(e.target.value)}
                  style={styles.commitInput}
                />
                <button
                  style={{
                    ...styles.button,
                    ...styles.primaryButton
                  }}
                  onClick={saveScript}
                  disabled={isLoading || !scriptContent.trim()}
                >
                  <Save size={16} />
                  Save Version
                </button>
              </div>
            </div>
            
            {/* Execution Preview */}
            {showPreview && renderExecutionPreview()}
          </div>
          
          {/* Version History Panel */}
          {showVersionHistory && renderVersionHistory()}
        </div>
      </div>
      
      {/* Verification Queue Modal */}
      {showVerificationQueue && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            width: '95%',
            maxWidth: '1400px',
            maxHeight: '90vh',
            backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
            borderRadius: '8px',
            border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '13px', 
                fontWeight: '600',
                color: isDarkMode ? '#E5E7EB' : '#1F2937'
              }}>
                Field Verification Queue
              </h2>
              <button
                onClick={() => setShowVerificationQueue(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  color: isDarkMode ? '#9CA3AF' : '#6B7280'
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <VerificationQueue
                checkResults={checkResults}
                data={Array.isArray(data) ? data : (data?.data || [])}
                respondentIdField={keyVariables.respondentId}
                collectorIdField={keyVariables.collectorId}
                interviewDateField={keyVariables.interviewDate}
                verifiedExceptions={verifiedExceptions}
                onExceptionsChange={setVerifiedExceptions}
                projectId={projectId}
                projectName={projectName || `Project ${projectId?.substring(0, 8) || 'Unknown'}`}
                columnTypes={columnTypes}
                xlsformData={xlsformData}
                onVerificationUpdate={(issueId, status, correction) => {
                  console.log('Verification update:', issueId, status, correction);
                  // Track corrections for script generation
                  if (status === 'corrected' && correction) {
                    // Extract issue details from the ID
                    const parts = issueId.split('_');
                    const type = parts[0];
                    const variable = parts.slice(1, -1).join('_');
                    const rowIndex = parseInt(parts[parts.length - 1]) || 0;
                    
                    setFieldCorrections(prev => ({
                      ...prev,
                      [issueId]: {
                        type,
                        variable,
                        rowIndex,
                        correction,
                        timestamp: new Date().toISOString()
                      }
                    }));
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CleaningStudio;